/** The paths a push, an email or a redirect opens: the server builds them here and the web app reads them on load. */

export const NOTIFICATION_LINK = {
  PARLAY: 'parlay',
  NOTIFICATIONS: 'notifications',
  SETTINGS: 'settings',
  ADMIN: 'admin',
} as const;

type EntryLinkKind = Exclude<
  (typeof NOTIFICATION_LINK)[keyof typeof NOTIFICATION_LINK],
  typeof NOTIFICATION_LINK.PARLAY
>;

export type NotificationLink =
  | { kind: typeof NOTIFICATION_LINK.PARLAY; parlayId: number }
  | { kind: EntryLinkKind };

/** Entry points into the app shell, not shareable tabs: each opens one view once on load. */
export const APP_ENTRY_PATH: Record<EntryLinkKind, string> = {
  [NOTIFICATION_LINK.NOTIFICATIONS]: '/notifications',
  [NOTIFICATION_LINK.SETTINGS]: '/settings',
  [NOTIFICATION_LINK.ADMIN]: '/admin',
};

function isParlayId(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

/** The URL a push carries: its parlay when it has one the app can open, else the notifications list. */
export function notificationLinkPath(parlayId: number | null | undefined): string {
  return isParlayId(parlayId)
    ? `/parlays/${parlayId}`
    : APP_ENTRY_PATH[NOTIFICATION_LINK.NOTIFICATIONS];
}

/** Reads a path the builders above produce; any other path is not a notification link. */
export function parseNotificationLink(pathname: string): NotificationLink | null {
  const entryKind = (
    [NOTIFICATION_LINK.NOTIFICATIONS, NOTIFICATION_LINK.SETTINGS, NOTIFICATION_LINK.ADMIN] as const
  ).find((kind) => APP_ENTRY_PATH[kind] === pathname);
  if (entryKind) return { kind: entryKind };
  const match = /^\/parlays\/([1-9]\d*)$/.exec(pathname);
  if (!match) return null;
  const parlayId = Number(match[1]);
  return isParlayId(parlayId) ? { kind: NOTIFICATION_LINK.PARLAY, parlayId } : null;
}
